/**
 * Client Factory
 * Factory pattern to create Supabase clients
 */

import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { SupabaseConfig } from "@/services/supabase/config";

/**
 * Options for creating clients
 */
export interface ClientOptions {
  persistSession?: boolean;
  autoRefreshToken?: boolean;
  /** When set, isolates auth storage so multiple accounts can use the same org (same Supabase project). */
  authStorageKey?: string;
}

/**
 * Creates a new Supabase client with the provided configuration
 *
 * @param config - Client configuration (url and anonKey)
 * @param options - Additional options for the client
 * @returns SupabaseClient
 */
export const createSupabaseClient = (
  config: SupabaseConfig,
  options?: ClientOptions
): SupabaseClient => {
  return createClient(config.url, config.anonKey, {
    auth: {
      persistSession: options?.persistSession ?? true,
      autoRefreshToken: options?.autoRefreshToken ?? true,
      ...(options?.authStorageKey ? { storageKey: options.authStorageKey } : {}),
    },
  });
};

/**
 * Creates an admin client with service key
 * Should only be used for admin and development operations
 *
 * @param url - Supabase URL
 * @param serviceKey - Service key (NOT anon key)
 * @returns SupabaseClient with admin permissions
 */
export const createAdminClient = (
  url: string,
  serviceKey: string
): SupabaseClient => {
  return createClient(url, serviceKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
  });
};
